import React, { useEffect } from "react";

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════
const numberToWords = (num) => {
  if (!num || isNaN(num)) return "Zero";
  const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const inWords = (n) => {
    if ((n = n.toString()).length > 9) return "Overflow";
    let nArray = ("000000000" + n).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!nArray) return "";
    let str = "";
    str += (Number(nArray[1]) !== 0) ? (a[Number(nArray[1])] || b[nArray[1][0]] + " " + a[nArray[1][1]]) + "Crore " : "";
    str += (Number(nArray[2]) !== 0) ? (a[Number(nArray[2])] || b[nArray[2][0]] + " " + a[nArray[2][1]]) + "Lakh " : "";
    str += (Number(nArray[3]) !== 0) ? (a[Number(nArray[3])] || b[nArray[3][0]] + " " + a[nArray[3][1]]) + "Thousand " : "";
    str += (Number(nArray[4]) !== 0) ? (a[Number(nArray[4])] || b[nArray[4][0]] + " " + a[nArray[4][1]]) + "Hundred " : "";
    str += (Number(nArray[5]) !== 0) ? ((str !== "") ? "and " : "") + (a[Number(nArray[5])] || b[nArray[5][0]] + " " + a[nArray[5][1]]) : "";
    return str.trim();
  };

  const wholeNumber = Math.floor(num);
  const decimal = Math.round((num - wholeNumber) * 100);
  let result = inWords(wholeNumber) + " Rupees";
  if (decimal > 0) result += " and " + inWords(decimal) + " Paise";
  return result + " Only";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", hour12: true
      });
    }
  } catch (e) { return ""; }
  return "";
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════
const MedicineDuePaymentReceiptModal = ({ show, receipt, company, onClose, onPrint }) => {
  useEffect(() => {
    if (show) document.body.classList.add("print-modal-open");
    else document.body.classList.remove("print-modal-open");
    return () => document.body.classList.remove("print-modal-open");
  }, [show]);

  if (!show || !receipt) return null;

  const customer = receipt;
  const comp = company || {};

  const handlePrint = () => {
    if (onPrint && typeof onPrint === 'function') onPrint();
    else window.print();
  };

  return (
    <>
      <style>{`
        /* PRINT STYLES */
        @media print {
          @page { margin: 0mm 5mm 5mm 5mm; size: A4 portrait; }
          body * { visibility: hidden; }
          body, html {
            background-color: #fff !important;
            margin: 0 !important; padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .dpr-printable, .dpr-printable * { visibility: visible !important; }
          .dpr-printable {
            position: absolute; left: 0; top: 0; width: 100%;
            margin: 0 !important; padding: 0 !important; background: white !important;
          }
          .dpr-overlay {
            position: static !important; background: none !important;
            padding: 0 !important; display: block !important; overflow: visible !important;
          }
          .dpr-card, .modal-content {
            max-width: 100% !important; width: 100% !important;
            margin: 0 !important; padding: 0 !important;
            border: none !important; box-shadow: none !important;
          }
          .dpr-no-print, .dpr-header, .dpr-footer { display: none !important; }
        }

        /* SCREEN OVERLAY */
        @media screen {
          .dpr-overlay {
            position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65);
            z-index: 1050; display: flex; align-items: flex-start; justify-content: center;
            padding: 16px; overflow-y: auto;
          }
          .dpr-card {
            display: flex; flex-direction: column; width: 100%; max-width: 850px;
            background: #fff; border-radius: 8px; overflow: hidden;
            box-shadow: 0 10px 40px rgba(0,0,0,0.35); margin: auto;
          }
          .dpr-header {
            flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
            padding: 10px 16px; background: #f8f9fa; border-bottom: 1px solid #dee2e6;
          }
          .dpr-body { flex: 1 1 auto; background: #e9ecef; padding: 16px; }
          .dpr-footer {
            flex: 0 0 auto; display: flex; align-items: center; justify-content: flex-end; gap: 10px;
            padding: 10px 16px; background: #f8f9fa; border-top: 2px solid #dee2e6;
          }
          .dpr-page {
            background: #fff; margin-bottom: 20px; padding: 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15); border: 1px solid #ccc;
          }
        }

        /* RECEIPT STYLES */
        .dpr-wrapper {
          font-family: Arial, Helvetica, sans-serif; color: #000;
          background: transparent; font-size: 11px;
        }
        .dpr-wrapper * { box-sizing: border-box; }

        .dpr-outer-box {
          border: 2px solid #000;
          display: flex; flex-direction: column;
          page-break-inside: avoid;
        }

        .dpr-title-strip {
          background-color: #ffc107 !important;
          border-bottom: 2px solid #000;
          text-align: center; padding: 4px;
          font-weight: 900; font-size: 13px; letter-spacing: 1px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .dpr-info-box { display: flex; border-bottom: 2px solid #000; }
        .dpr-info-left { width: 60%; border-right: 2px solid #000; padding: 5px 10px; }
        .dpr-info-right { width: 40%; padding: 5px 10px; }
        .dpr-info-box table { width: 100%; font-size: 10px; line-height: 1.4; }
        .dpr-info-box td { vertical-align: top; }

        .dpr-table {
          width: 100%; border-collapse: collapse; font-size: 10px;
        }
        .dpr-table th {
          background-color: #e0e0e0 !important;
          border-right: 1px solid #000; border-bottom: 2px solid #000;
          padding: 4px 6px; font-weight: bold; font-size: 9px;
          text-transform: uppercase;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .dpr-table th:last-child { border-right: none; }
        .dpr-table td {
          border-right: 1px solid #ccc; border-bottom: 1px solid #ccc;
          padding: 3px 6px;
        }
        .dpr-table td:last-child { border-right: none; }

        .dpr-footer-grid { display: flex; border-top: 2px solid #000; }
        .dpr-words-box {
          width: 60%; border-right: 2px solid #000; padding: 6px 10px;
          display: flex; flex-direction: column; justify-content: space-between;
        }
        .dpr-calc-box { width: 40%; }
        .dpr-calc-table { width: 100%; border-collapse: collapse; }
        .dpr-calc-table td {
          padding: 3px 8px; border-bottom: 1px solid #ccc; font-size: 10px;
        }
        .dpr-calc-table tr:last-child td { border-bottom: none; }
        .dpr-calc-highlight td {
          background-color: #d4edda !important;
          font-weight: bold;
          -webkit-print-color-adjust: exact !important;
        }

        .dpr-signature {
          display: flex; justify-content: space-between;
          padding: 20px 20px 5px 20px;
          border-top: 2px solid #000; background: #fff;
        }
        .dpr-sig-line {
          border-top: 1px solid #000; padding-top: 3px;
          text-align: center; font-weight: bold; font-size: 9px;
          width: 45%;
        }

        .dpr-status-paid {
          background: #28a745 !important; color: white;
          padding: 1px 6px; border-radius: 3px;
          font-size: 9px; font-weight: bold;
          -webkit-print-color-adjust: exact !important;
        }
        .dpr-status-partial {
          background: #ffc107 !important; color: #000;
          padding: 1px 6px; border-radius: 3px;
          font-size: 9px; font-weight: bold;
          -webkit-print-color-adjust: exact !important;
        }
      `}</style>

      {/* ═══ OVERLAY ═══ */}
      <div className="dpr-overlay">
        <div className="dpr-card">

          {/* HEADER (no-print) */}
          <div className="dpr-header dpr-no-print">
            <h5 className="mb-0 fs-6 fw-bold">
              💰 Due Payment Receipt Preview
            </h5>
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          {/* BODY */}
          <div className="dpr-body dpr-printable">
            <div className="dpr-wrapper">
              <div className="dpr-page">
                <div className="dpr-outer-box">

                  {/* COMPANY HEADER */}
                  <div className="d-flex border-bottom border-dark">
                    {comp.logoBase64 && (
                      <div
                        className="p-2 d-flex justify-content-center align-items-center"
                        style={{ width: "80px", borderRight: "1px solid #000" }}
                      >
                        <img
                          src={`data:image/jpeg;base64,${comp.logoBase64}`}
                          alt="Logo"
                          style={{ maxWidth: "100%", maxHeight: "50px", objectFit: "contain" }}
                        />
                      </div>
                    )}
                    <div className="p-2 flex-grow-1">
                      <h4 className="mb-0 fw-bold" style={{ fontSize: "14px", textTransform: "uppercase" }}>
                        {comp.name || comp.company_Name || "PHARMACY"}
                      </h4>
                      <div style={{ fontSize: "9px" }}>
                        {comp.address && (
                          <div>
                            {comp.address}
                            {comp.district && `, ${comp.district}`}
                            {comp.state && ` (${comp.stateCode || comp.state})`}
                          </div>
                        )}
                        <div>
                          {comp.contact1 && `Ph: ${comp.contact1} `}
                          {comp.email && `| Email: ${comp.email}`}
                        </div>
                        {comp.regNo && <div><strong>D.L. No:</strong> {comp.regNo}</div>}
                      </div>
                    </div>
                  </div>

                  {/* TITLE */}
                  <div className="dpr-title-strip">
                    DUE PAYMENT RECEIPT
                  </div>

                  {/* CUSTOMER & RECEIPT INFO */}
                  <div className="dpr-info-box">
                    <div className="dpr-info-left">
                      <table>
                        <tbody>
                          <tr>
                            <td width="22%"><strong>Received From</strong></td>
                            <td width="3%">:</td>
                            <td><strong style={{ fontSize: '11px' }}>{customer.customerName || "Cash Customer"}</strong></td>
                          </tr>
                          <tr>
                            <td><strong>Mobile</strong></td>
                            <td>:</td>
                            <td>{customer.mobileNo || "-"}</td>
                          </tr>
                          {customer.customerType && (
                            <tr>
                              <td><strong>Patient Type</strong></td>
                              <td>:</td>
                              <td>{customer.customerType}</td>
                            </tr>
                          )}
                          <tr>
                            <td><strong>Payment Mode</strong></td>
                            <td>:</td>
                            <td><strong>{customer.payMode}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="dpr-info-right">
                      <table style={{ width: "auto", marginLeft: "auto", fontSize: "10px" }}>
                        <tbody>
                          <tr>
                            <td style={{ textAlign: "right", paddingRight: "5px" }}>
                              <strong>Receipt No</strong>
                            </td>
                            <td>: <span className="fw-bold">{customer.receiptNo}</span></td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "right", paddingRight: "5px" }}>
                              <strong>Date</strong>
                            </td>
                            <td>: {formatDate(customer.receiptDate)}</td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "right", paddingRight: "5px" }}>
                              <strong>Time</strong>
                            </td>
                            <td>: {formatTime(customer.receiptDate)}</td>
                          </tr>
                          {customer.entryBy && (
                            <tr>
                              <td style={{ textAlign: "right", paddingRight: "5px" }}>
                                <strong>Entry By</strong>
                              </td>
                              <td>: {customer.entryBy}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ALLOCATIONS TABLE */}
                  <table className="dpr-table">
                    <thead>
                      <tr>
                        <th style={{ width: '5%', textAlign: 'center' }}>SN</th>
                        <th style={{ width: '18%', textAlign: 'left' }}>Bill No</th>
                        <th style={{ width: '13%', textAlign: 'center' }}>Bill Date</th>
                        <th style={{ width: '13%', textAlign: 'right' }}>Bill Amount</th>
                        <th style={{ width: '13%', textAlign: 'right' }}>Due Before</th>
                        <th style={{ width: '14%', textAlign: 'right' }}>Paid Now</th>
                        <th style={{ width: '13%', textAlign: 'right' }}>Due After</th>
                        <th style={{ width: '11%', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(customer.allocations || []).map((a, idx) => (
                        <tr key={idx}>
                          <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                          <td style={{ textAlign: 'left' }}><strong>{a.billNo}</strong></td>
                          <td style={{ textAlign: 'center' }}>{formatDate(a.billDate)}</td>
                          <td style={{ textAlign: 'right' }}>{Number(a.billNetAmount || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'right' }}>{Number(a.billDueBefore || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            {Number(a.allocatedAmount || 0).toFixed(2)}
                          </td>
                          <td style={{ textAlign: 'right' }}>{Number(a.billDueAfter || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={
                              a.paymentStatusAfter === 'PAID'
                                ? 'dpr-status-paid'
                                : 'dpr-status-partial'
                            }>
                              {a.paymentStatusAfter}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* FOOTER WITH TOTALS */}
                  <div className="dpr-footer-grid">
                    <div className="dpr-words-box">
                      <div>
                        <div className="mb-1" style={{ fontSize: "10px" }}>
                          <strong>Amount in Words:</strong><br />
                          {numberToWords(customer.totalAmount)}
                        </div>
                        {customer.remarks && (
                          <div className="mt-2" style={{ fontSize: "9px" }}>
                            <strong>Remarks:</strong> {customer.remarks}
                          </div>
                        )}
                        <div className="mt-2" style={{ fontSize: "8px", color: "#666" }}>
                          <strong>Note:</strong> This is a payment receipt against outstanding bills.
                          Original tax invoice details remain unchanged.
                        </div>
                      </div>
                    </div>

                    <div className="dpr-calc-box">
                      <table className="dpr-calc-table">
                        <tbody>
                          <tr>
                            <td>Cash Received:</td>
                            <td className="text-end">₹ {Number(customer.cashAmount || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>UPI Received:</td>
                            <td className="text-end">₹ {Number(customer.upiAmount || 0).toFixed(2)}</td>
                          </tr>
                          <tr className="dpr-calc-highlight">
                            <td><strong>TOTAL RECEIVED:</strong></td>
                            <td className="text-end">
                              <strong>₹ {Number(customer.totalAmount || 0).toFixed(2)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="2" className="text-center" style={{ paddingTop: '15px' }}>
                              <div style={{
                                borderTop: "1px solid #000",
                                margin: "0 auto",
                                width: "85%",
                                paddingTop: "3px",
                                fontSize: "9px",
                                fontWeight: "bold"
                              }}>
                                Authorized Signatory
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* FOOTER BUTTONS (no-print) */}
          <div className="dpr-footer dpr-no-print">
            <button className="btn btn-secondary btn-sm px-3" onClick={onClose}>
              <i className="bi bi-x-circle me-1"></i> Close
            </button>
            <button
              className="btn btn-primary btn-sm px-4 fw-bold"
              onClick={handlePrint}
              style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}
            >
              <i className="bi bi-printer me-2"></i> Print Receipt
            </button>
          </div>

        </div>
      </div>
    </>
  );
};
 export default MedicineDuePaymentReceiptModal;;